import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../supabase';
import { useAdmin } from './AdminContext';
import { REALMS } from '../../../../shared/src/realms';
import { useSearchParams } from 'react-router-dom';

interface QuestionRow {
  id: string; realm: string; sub_topic: string | null; difficulty: string;
  question_text: string; choices: string[]; correct_index: number;
  explanation: string | null; active: boolean; reviewed: boolean;
  times_served: number; times_correct: number;
}

export function QuestionDatabase() {
  const { refreshStats, toast } = useAdmin();
  const [searchParams] = useSearchParams();

  const [questions, setQuestions] = useState<QuestionRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [realmFilter, setRealmFilter] = useState(searchParams.get('realm') || 'ALL');
  const [diffFilter, setDiffFilter] = useState('ALL');
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(0);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editData, setEditData] = useState<Partial<QuestionRow>>({});

  const fetchQuestions = useCallback(async () => {
    setLoading(true);
    // Full rows (answer key included) only exist behind the is_admin()-gated
    // admin_questions function; direct table SELECT no longer exposes them.
    const { data, error } = await supabase.rpc('admin_questions', {
      p_realm: realmFilter !== 'ALL' ? realmFilter : null,
      p_difficulty: diffFilter !== 'ALL' ? diffFilter : null,
      p_search: search || null,
      p_order: 'created_desc',
      p_limit: pageSize,
      p_offset: page * pageSize,
    });
    if (error) toast(error.message, 'error');
    else setQuestions(data || []);
    setLoading(false);
  }, [realmFilter, diffFilter, search, page, pageSize, toast]);

  useEffect(() => { fetchQuestions(); }, [fetchQuestions]);

  const deleteQuestion = async (id: string) => {
    if (!window.confirm('Delete this question permanently?')) return;
    const { error } = await supabase.from('questions').delete().eq('id', id);
    if (!error) {
      setQuestions(prev => prev.filter(q => q.id !== id));
      toast('Question deleted', 'info');
      refreshStats();
    }
  };

  const saveEdit = async (id: string) => {
    const { error } = await supabase.from('questions').update(editData).eq('id', id);
    if (error) { toast(error.message, 'error'); return; }
    setQuestions(prev => prev.map(q => q.id === id ? { ...q, ...editData } : q));
    setEditingId(null);
    setEditData({});
    toast('Question updated', 'success');
    refreshStats();
  };

  const updateDifficulty = async (id: string, diff: string) => {
    const { error } = await supabase.from('questions').update({ difficulty: diff }).eq('id', id);
    if (!error) {
      setQuestions(prev => prev.map(q => q.id === id ? { ...q, difficulty: diff } : q));
      toast(`Difficulty → ${diff}`, 'info');
    }
  };

  return (
    <div>
      <h2 className="admin-section-title" style={{ marginBottom: '20px' }}>QUESTION DATABASE</h2>

      {/* ── Toolbar ── */}
      <div className="admin-card" style={{ marginBottom: '20px', padding: '16px' }}>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          <input className="admin-input" placeholder="🔍 Search questions..." value={search}
            onChange={e => { setSearch(e.target.value); setPage(0); }}
            style={{ maxWidth: '300px' }} />

          <select className="admin-select" value={realmFilter} onChange={e => { setRealmFilter(e.target.value); setPage(0); }}
            style={{ width: '160px' }}>
            <option value="ALL">All Realms</option>
            {Object.entries(REALMS).map(([k, v]) => <option key={k} value={k}>{v.name}</option>)}
          </select>

          <select className="admin-select" value={diffFilter} onChange={e => { setDiffFilter(e.target.value); setPage(0); }}
            style={{ width: '120px' }}>
            <option value="ALL">All Diff</option>
            <option value="easy">Easy</option>
            <option value="medium">Medium</option>
            <option value="hard">Hard</option>
          </select>

          <select className="admin-select" value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(0); }}
            style={{ width: '80px' }}>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>

          <button className="admin-btn admin-btn-secondary admin-btn-sm" onClick={fetchQuestions}>↻</button>
        </div>
      </div>

      {/* ── Table ── */}
      <div className="admin-card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table className="admin-table">
            <thead>
              <tr>
                <th style={{ width: '90px' }}>REALM</th>
                <th style={{ width: '70px' }}>DIFF</th>
                <th>QUESTION</th>
                <th style={{ width: '80px', textAlign: 'center' }}>STATS</th>
                <th style={{ width: '100px', textAlign: 'right' }}>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} style={{ padding: '40px', textAlign: 'center', color: '#808080' }}>Loading...</td></tr>
              ) : questions.length === 0 ? (
                <tr><td colSpan={5} style={{ padding: '40px', textAlign: 'center', color: '#808080' }}>No questions found.</td></tr>
              ) : questions.map(q => (
                <>
                  <tr key={q.id} style={{ cursor: 'pointer' }} onClick={() => setExpandedId(expandedId === q.id ? null : q.id)}>
                    <td style={{ color: REALMS[q.realm as keyof typeof REALMS]?.color || '#fff', fontWeight: 'bold', fontSize: '0.85rem', letterSpacing: '0.05em' }}>
                      {q.realm}
                    </td>
                    <td>
                      <select className="admin-badge"
                        style={{ cursor: 'pointer', background: 'transparent' }}
                        value={q.difficulty}
                        onClick={e => e.stopPropagation()}
                        onChange={e => { e.stopPropagation(); updateDifficulty(q.id, e.target.value); }}>
                        <option value="easy">EASY</option>
                        <option value="medium">MED</option>
                        <option value="hard">HARD</option>
                      </select>
                    </td>
                    <td style={{ color: '#dcdce0', fontSize: '0.85rem', maxWidth: '400px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {q.question_text}
                    </td>
                    <td style={{ textAlign: 'center', fontFamily: 'var(--font-mono)', fontSize: '0.85rem', color: '#808080' }}>
                      {q.times_served > 0
                        ? `${Math.round(q.times_correct / q.times_served * 100)}%`
                        : '—'}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <button className="admin-btn admin-btn-danger admin-btn-sm"
                        onClick={e => { e.stopPropagation(); deleteQuestion(q.id); }}>
                        🗑️
                      </button>
                    </td>
                  </tr>
                  {expandedId === q.id && (
                    <tr key={`${q.id}-expand`}>
                      <td colSpan={5} style={{ padding: '16px 24px', background: '#080808', borderBottom: '1px solid #243450' }}>
                        {editingId === q.id ? (
                          /* Edit mode */
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            <textarea className="admin-input" rows={3}
                              value={editData.question_text ?? q.question_text}
                              onChange={e => setEditData({ ...editData, question_text: e.target.value })}
                              style={{ fontFamily: 'inherit' }} />
                            {q.choices.map((c, i) => (
                              <div key={i} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                <input type="radio" name="correct" checked={(editData.correct_index ?? q.correct_index) === i}
                                  onChange={() => setEditData({ ...editData, correct_index: i })}
                                  style={{ accentColor: '#14b8a6' }} />
                                <input className="admin-input"
                                  value={(editData.choices ?? q.choices)[i]}
                                  onChange={e => {
                                    const newChoices = [...(editData.choices ?? q.choices)];
                                    newChoices[i] = e.target.value;
                                    setEditData({ ...editData, choices: newChoices });
                                  }} />
                              </div>
                            ))}
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <button className="admin-btn admin-btn-success admin-btn-sm" onClick={() => saveEdit(q.id)}>SAVE</button>
                              <button className="admin-btn admin-btn-secondary admin-btn-sm" onClick={() => { setEditingId(null); setEditData({}); }}>CANCEL</button>
                            </div>
                          </div>
                        ) : (
                          /* View mode */
                          <div>
                            <div style={{ fontSize: '0.95rem', color: '#eee', marginBottom: '12px', lineHeight: 1.5 }}>{q.question_text}</div>
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
                              {q.choices.map((c, i) => (
                                <div key={i} style={{
                                  padding: '6px 12px', borderRadius: '6px', fontSize: '0.85rem',
                                  background: i === q.correct_index ? '#14b8a618' : '#16223A',
                                  color: i === q.correct_index ? '#14b8a6' : '#ccccd4',
                                  border: `1px solid ${i === q.correct_index ? '#14b8a644' : '#243450'}`
                                }}>
                                  <span style={{ fontSize: '0.8rem', color: '#808080', marginRight: '4px' }}>{['A', 'B', 'C', 'D'][i]}</span> {c}
                                </div>
                              ))}
                            </div>
                            {q.explanation && <div style={{ fontSize: '0.8rem', color: '#808080', fontStyle: 'italic', marginBottom: '8px' }}>💡 {q.explanation}</div>}
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                              <button className="admin-btn admin-btn-secondary admin-btn-sm"
                                onClick={() => { setEditingId(q.id); setEditData({}); }}>
                                ✎ EDIT
                              </button>
                              <span style={{ fontSize: '0.85rem', fontFamily: 'var(--font-mono)', color: '#444' }}>
                                Served: {q.times_served} · Correct: {q.times_correct} · {q.sub_topic || 'no topic'} · {q.reviewed ? '✓ reviewed' : '⏳ pending'}
                              </span>
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Pagination ── */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', alignItems: 'center', marginTop: '16px' }}>
        <button className="admin-btn admin-btn-secondary admin-btn-sm" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}>
          ← PREV
        </button>
        <span style={{ fontSize: '0.8rem', color: '#808080', fontFamily: 'var(--font-mono)' }}>PAGE {page + 1}</span>
        <button className="admin-btn admin-btn-secondary admin-btn-sm" onClick={() => setPage(p => p + 1)} disabled={questions.length < pageSize}>
          NEXT →
        </button>
      </div>
    </div>
  );
}
